import type { BadgeVariant } from '../../components/ui';
import type { KhoaDich } from '../../i18n';
import type { MaDonVi } from './nghiep-vu/don-vi';
import type { MucDoThongBao, ThongBao, TrangThaiCanhBao, TrangThaiDongBo } from './types';

/**
 * Bảng tông và nhãn của phân hệ Bảo mật.
 *
 * Màu trạng thái quyết ở đây, **không** ở `src/mock/` (tầng đó bị xoá khi có
 * API thật). Chỉ dùng nhóm biến thể **trạng thái** của `Badge`: mọi thứ ở đây
 * là mức độ hoặc tình trạng, không phải phân loại.
 */

/** Mã đơn vị (`nghiep-vu/don-vi.ts`) → khoá dịch. */
export const DON_VI: Record<MaDonVi, KhoaDich> = {
  phut: 'caiDat.donVi.phut',
  lan: 'caiDat.donVi.lan',
  kyTu: 'caiDat.donVi.kyTu',
  ngay: 'caiDat.donVi.ngay',
  thang: 'caiDat.donVi.thang',
  suKienS: 'caiDat.donVi.suKienS',
};

/** Đồng bộ chính sách mật khẩu vào Keycloak (ADR-0059 mục 5.2). */
export const TRANG_THAI_DONG_BO: Record<TrangThaiDongBo, { khoa: KhoaDich; variant: BadgeVariant }> = {
  SYNCED: { khoa: 'chinhSach.dongBo.SYNCED', variant: 'success' },
  PENDING: { khoa: 'chinhSach.dongBo.PENDING', variant: 'warning' },
  // Tắt có chủ đích (client realm-config chưa bật): không phải lỗi, nên xám.
  DISABLED: { khoa: 'chinhSach.dongBo.DISABLED', variant: 'neutral' },
};

/** Kết quả gửi một thông báo. `NO_RECIPIENT` cần người vào khai người nhận. */
export const TRANG_THAI_THONG_BAO: Record<ThongBao['status'], { khoa: KhoaDich; variant: BadgeVariant }> = {
  SENT: { khoa: 'thongBao.trangThai.SENT', variant: 'success' },
  NO_RECIPIENT: { khoa: 'thongBao.trangThai.NO_RECIPIENT', variant: 'warning' },
  FAILED: { khoa: 'thongBao.trangThai.FAILED', variant: 'danger' },
};

export const MUC_DO_THONG_BAO: Record<MucDoThongBao, { khoa: KhoaDich; variant: BadgeVariant }> = {
  INFO: { khoa: 'caiDat.mucDo.INFO', variant: 'neutral' },
  WARNING: { khoa: 'caiDat.mucDo.WARNING', variant: 'warning' },
  CRITICAL: { khoa: 'caiDat.mucDo.CRITICAL', variant: 'danger' },
};

/**
 * Trạng thái cảnh báo audit đã kích hoạt (ADR-0059 mục 4.1). Chưa xem là việc
 * cần người vào xử lý nên vàng; đã xem thì xám, không phải "đã khỏi".
 */
export const TRANG_THAI_CANH_BAO: Record<TrangThaiCanhBao, { khoa: KhoaDich; variant: BadgeVariant }> = {
  OPEN: { khoa: 'canhBaoAudit.trangThai.OPEN', variant: 'warning' },
  ACKNOWLEDGED: { khoa: 'canhBaoAudit.trangThai.ACKNOWLEDGED', variant: 'neutral' },
};

/** Khoá thông báo ở tầng nền tảng, theo thứ tự hiện ở màn Chính sách nền tảng. */
export const KHOA_THONG_BAO_NEN_TANG = [
  'notify.security_critical',
  'notify.ops_warning',
  'notify.license_expiry',
] as const;

/**
 * Nhãn của dòng blocklist chặn tự động (ADR-0090 mục 3.2). Đây là **phân loại** (ai thêm dòng này), không phải mức
 * độ, nên dùng tông phân loại `violet` của `Badge`: tô `danger` sẽ đọc thành "dòng này nguy hiểm hơn dòng chặn tay".
 */
export const BADGE_CHAN_TU_DONG: { khoa: KhoaDich; variant: BadgeVariant } = {
  khoa: 'baoMat.chan.tuDong',
  variant: 'violet',
};

/**
 * Tham số được hiện **một câu hậu quả** dưới nhãn (`caiDat.<khoá>.moTa`). Mọi tham số khác chỉ có nhãn: nhãn đủ
 * nghĩa, biên hợp lệ đã nằm ngay dưới ô nhập, nên một đoạn mô tả dưới mỗi dòng chỉ làm màn rối ("không nhồi nhét
 * chữ"). Chỉ thêm khoá vào đây khi nhãn không nói được một hậu quả nghiêm trọng (khoá người dùng, mất token…), hay
 * khi ô cần một quy ước nhập mà biên không diễn đạt được; câu mô tả giữ đúng một câu ngắn.
 */
export const KHOA_CO_CAU_HAU_QUA: ReadonlySet<string> = new Set([
  'auth.mfa_required',
  'network.mfa_ngoai_dai',
  'audit.login_error_burst_per_ip',
  'ai_quota.uu_tien',
  'contact.types',
  'license.approval.special_features',
]);

/** Số giờ của báo cáo IP ngoài dải tin cậy ở mức Chỉ ghi nhận. */
export const GIO_IP_MONITOR = 72;

/** Số dòng blocklist còn hiệu lực tối đa của một tenant và của nền tảng (`MAX_ACTIVE`, ADR-0090 mục 3.1). */
export const TRAN_CHAN_IP = 200;

/**
 * Tông nhãn **nguồn giá trị** cạnh mỗi tham số (ADR-0059 mục 8).
 *
 * Ba mức, đều là trạng thái của giá trị so với tầng đang xem:
 * - đang sửa, chưa lưu → `warning` (việc còn dở);
 * - đặt ở chính tầng này → `primary` (đã đè lên mức kế thừa);
 * - kế thừa (mặc định, nền tảng, gói) → `neutral`.
 */
export function tongNguon(dangSua: boolean, datOTangNay: boolean): BadgeVariant {
  if (dangSua) return 'warning';
  return datOTangNay ? 'primary' : 'neutral';
}
